import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { ChartContainer, type ChartConfig } from '@/shared/components/ui/chart';
import type { MemberTableEntry } from '../types';

const chartConfig = {
  gainLoss: { label: 'Ganancia / Pérdida' },
} satisfies ChartConfig;

const gainLossFormatter = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  maximumFractionDigits: 0,
});

function formatK(value: number): string {
  if (Math.abs(value) >= 1000) return `$${(value / 1000).toFixed(1)}k`;
  return `$${value}`;
}

interface GainLossBarChartProps {
  entries: MemberTableEntry[];
}

export function GainLossBarChart({ entries }: GainLossBarChartProps) {
  const { t } = useTranslation('laborPerformance');

  const data = entries.map((e) => ({
    name: e.memberName.split(' ')[0],
    gainLoss: e.gainLoss,
    memberId: e.memberId,
  }));

  return (
    <div className="rounded-lg border bg-card p-4" data-testid="labor-performance-gain-loss-chart">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-semibold">{t('chart.gainLossTitle')}</span>
        <span className="text-xs text-muted-foreground">{t('chart.gainLossSubtitle')}</span>
      </div>
      <ChartContainer config={chartConfig} className="h-[220px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 0, right: 40, bottom: 0, left: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" tickFormatter={formatK} tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="name" width={72} tick={{ fontSize: 12 }} />
            <Tooltip
              formatter={(v) => [gainLossFormatter.format(Number(v)), t('chart.gainLossSubtitle')]}
            />
            <Bar dataKey="gainLoss" radius={[0, 4, 4, 0]}>
              {data.map((entry) => (
                <Cell key={entry.memberId} fill={entry.gainLoss >= 0 ? '#22c55e' : '#ef4444'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartContainer>
    </div>
  );
}
